<script setup lang="ts">
import { computed, defineAsyncComponent, watch, onMounted } from 'vue'
import { useRoute, RouterView } from 'vue-router'
import AppLayout from '@/layouts/AppLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import PageLoader from '@/components/ui/PageLoader.vue'
import ToastHost from '@/components/ui/ToastHost.vue'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceStore } from '@/stores/workspace'
import { resolveLocale, setLocale } from '@/i18n'

const AIAssistantPanel = defineAsyncComponent(() => import('@/components/ai/AIAssistantPanel.vue'))
const SocialConnectionConsentHost = defineAsyncComponent(() => import('@/components/integrations/SocialConnectionConsentHost.vue'))

const route = useRoute()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()

// When user language is 'auto', sync the UI locale to the active workspace language
watch(
  [() => authStore.user?.language, () => workspaceStore.currentWorkspace],
  ([userLang, workspace]) => {
    if (userLang === 'auto' && workspace) {
      setLocale(resolveLocale('auto', workspace.language))
    }
  },
  { immediate: true }
)

// Fade out the static app loader from index.html once the app is mounted
// Skip on editor pages — they have their own PageLoader experience
onMounted(() => {
  const loader = document.getElementById('app-loader')
  if (!loader) return
  const isEditor = /\/\d+\/\d+\/editor/.test(window.location.pathname)
  if (isEditor) {
    loader.remove()
    return
  }
  const elapsed = performance.now()
  const minDelay = Math.max(0, 1000 - elapsed)
  setTimeout(() => {
    loader.classList.add('fade-out')
    loader.addEventListener('transitionend', () => loader.remove())
  }, minDelay)
})

// Determine which layout to use based on route meta
const layoutType = computed(() => {
  return route.meta?.layout as string | undefined
})

const layout = computed(() => {
  if (layoutType.value === 'auth') {
    return AuthLayout
  }
  if (layoutType.value === 'app') {
    return AppLayout
  }
  // For 'public', 'none', or undefined - no layout wrapper
  return null
})

// The assistant is only offered on screens that belong to a team: it reads the
// team from the web address, so on the sign-up / create-a-team / not-found
// screens there is nothing for it to work with. Those screens also don't make
// room for it on the right, which is now the only way it is ever shown.
const showAssistant = computed(() =>
  layoutType.value !== 'public' && layoutType.value !== 'auth'
)
</script>

<template>
  <component v-if="layout" :is="layout">
    <RouterView />
  </component>
  <RouterView v-else />

  <!-- Unified page loader — persists across route changes -->
  <PageLoader />

  <!-- App-wide toast surface (publish confirmations, publish failures, …) -->
  <ToastHost />

  <SocialConnectionConsentHost v-if="authStore.isAuthenticated" />

  <!-- AI Assistant Panel — on every signed-in team screen, editors included -->
  <AIAssistantPanel v-if="authStore.isAuthenticated && showAssistant" />
</template>

<style>
/* Global app styles are in assets/styles/main.css */
</style>
